import { BlockModule } from '../common'
import { searchLocation } from '@/api/location'
import { useMsigColorMode } from '@/composables'
import type { BranchSearchResult } from '@/types'

new BlockModule({
    selector: '.general-locator-block',
    async setupBlock(el, onTeardown, { onInView }) {
        const apiKey = el.getAttribute('data-google-maps-api-key')
        const contentKey = el.getAttribute('data-content-key')
        const branchLocatorEl = el.querySelector('.general-locator-vue-app')
        console.log('load')
        if (!apiKey || !branchLocatorEl) return

        const { store: colorStore } = useMsigColorMode()

        const setupGoogleMaps = async () => {
            const { getInfoWindowContent } = await import('./utils')

            const {
                createApp,
                defineComponent,
                defineAsyncComponent,
                ref,
                watch,
                computed,
                nextTick,
                onMounted,
                onUnmounted
            } = await import('vue')

            const { watchPausable, useBreakpoints } = await import('@vueuse/core')
            const { tailwindBreakpoints } = await import('@/modules/website/tailwind')

            const branchLocatorVueApp = createApp(
                defineComponent({
                    name: 'general-locator-vue-app',
                    components: {
                        MsigButton: defineAsyncComponent(
                            () => import('@/components/website/button/button.vue')
                        ),
                        ScrollArea: defineAsyncComponent(
                            () => import('@/components/ui/scroll-area/ScrollArea.vue')
                        ),
                        ScrollBar: defineAsyncComponent(
                            () => import('@/components/ui/scroll-area/ScrollBar.vue')
                        ),
                        RadioGroupRoot: defineAsyncComponent(
                            () => import('radix-vue').then(x => x.RadioGroupRoot) 
                        ),
                        RadioGroupItem: defineAsyncComponent(
                            () => import('radix-vue').then(x => x.RadioGroupItem) 
                        ),
                        RadioGroupIndicator: defineAsyncComponent(
                            () => import('radix-vue').then(x => x.RadioGroupIndicator) 
                        ),
                        FadeInOut: defineAsyncComponent(
                            () => import('@/components/website/transition/FadeInOut.vue')
                        ),
                        LocationDrawer: defineAsyncComponent(
                            () => import('./components/LocationDrawer.vue')
                        ),
                        Drawer: defineAsyncComponent(() => import('@/components/ui/drawer/Drawer.vue')),
                        DrawerTrigger: defineAsyncComponent(
                            () => import('vaul-vue').then(x => x.DrawerTrigger)
                        ),
                        DrawerContent: defineAsyncComponent(
                            () => import('@/components/ui/drawer/DrawerContent.vue')
                        ),
                        DrawerTitle: defineAsyncComponent(
                            () => import('radix-vue').then(x => x.DialogTitle)
                        ),
                        DrawerDescription: defineAsyncComponent(
                            () => import('radix-vue').then(x => x.DialogDescription)
                        )
                    },
                    setup() {
                        const isBusy = ref(false)
                        const branches = ref<BranchSearchResult[]>([])
                        const googleMapsRef = ref<HTMLElement|null>(null)
                        const searchInputRef = ref<HTMLElement|null>(null)

                        let customMarker: google.maps.marker.AdvancedMarkerElement
                        let googleMap: google.maps.Map
                        let infoWindow: google.maps.InfoWindow
                                           
                        const searchTerm = ref('')
                        const selectedBranchKey = ref('')

                        const currentPage = ref(1)
                        const totalPages = ref(1)
                        const isLoadingMore = ref(false)
                        const pageSize = 12

                        const breakpoints = useBreakpoints(tailwindBreakpoints)
                        const mdOrGreater = breakpoints.greaterOrEqual('md')
                        const isDrawerOpen = ref(false)
                        const hasSearchedOnce = ref(false)

                        const selectedBranch = computed(() => branches.value.find(x => x.key === selectedBranchKey.value))

                        const infoWindowContentString = computed(() => {
                            if (!selectedBranch.value) return ''
                            return getInfoWindowContent(
                                selectedBranch.value.title, 
                                selectedBranch.value.branchAddress, 
                                selectedBranch.value.linkName,
                                selectedBranch.value.linkUrl
                            )
                        })

                        const initGoogleMaps = async () => {
                            if (!googleMapsRef.value) return

                            await import('./index.css')
                            const { Loader } = await import('@googlemaps/js-api-loader')
                            const loader = new Loader({
                                apiKey,
                                version: 'weekly',
                                libraries: ['places']
                            })
                            const { Map } = await loader.importLibrary('maps')
                            const { AdvancedMarkerElement } = await loader.importLibrary('marker')
                            const { ColorScheme } = await loader.importLibrary('core')

                            googleMap = new Map(googleMapsRef.value, {
                                center: {
                                    lat: selectedBranch.value?.latitude || 0,
                                    lng: selectedBranch.value?.longitude || 0
                                },
                                zoom: 17,
                                mapId: 'DEMO_MAP_ID',
                                colorScheme: colorStore.value === 'light' ? ColorScheme.LIGHT : ColorScheme.DARK
                            })
                            customMarker = new AdvancedMarkerElement({
                                map: googleMap,
                                position: {
                                    lat: selectedBranch.value?.latitude || 0,
                                    lng: selectedBranch.value?.longitude || 0
                                },
                            })
                            infoWindow = new google.maps.InfoWindow({
                                content: infoWindowContentString.value
                            })
                            infoWindow.open({
                                anchor: customMarker,
                                map: googleMap
                            })
                        }

                        const withFallbackKeys = (items: BranchSearchResult[], offset = 0) =>
                            items.map((item, i) => ({ ...item, key: item.key || `branch-${offset + i}` }))

                        const onSearch = async () => {
                            isBusy.value = true
                            currentPage.value = 1

                            try {
                                const data = await searchLocation(searchTerm.value, contentKey ?? '', 1, pageSize)
                                branches.value = withFallbackKeys(data.items)
                                totalPages.value = data.totalPages

                                if (branches.value.length > 0) {
                                    if (mdOrGreater.value) {
                                        const firstBranch = branches.value[0]
                                        selectedBranchKey.value = firstBranch.key
                                    } else if (!hasSearchedOnce.value) {
                                        const firstBranch = branches.value[0]
                                        selectedBranchKey.value = firstBranch.key
                                        hasSearchedOnce.value = true
                                    }
                                }
                            } finally {
                                isBusy.value = false
                            }
                        }

                        const loadMore = async () => {
                            if (isBusy.value || isLoadingMore.value) return
                            if (currentPage.value >= totalPages.value) return

                            isLoadingMore.value = true
                            try {
                                const nextPage = currentPage.value + 1
                                const data = await searchLocation(searchTerm.value, contentKey ?? '', nextPage, pageSize)
                                branches.value.push(...withFallbackKeys(data.items, branches.value.length))
                                currentPage.value = nextPage
                                totalPages.value = data.totalPages
                            } finally {
                                isLoadingMore.value = false
                            }
                        }

                        const handleScroll = (e: Event) => {
                            const target = e.target as HTMLElement | null
                            if (!target?.matches?.('[data-radix-scroll-area-viewport]')) return
                            if (!target.closest('.general-locator-scroll-area')) return
                            if (target.scrollTop + target.clientHeight >= target.scrollHeight - 100) {
                                loadMore()
                            }
                        }

                        onMounted(() => {
                            document.addEventListener('scroll', handleScroll, true)
                        })

                        onUnmounted(() => {
                            document.removeEventListener('scroll', handleScroll, true)
                        })

                        const { stop: stopWatching } = watchPausable([googleMapsRef, () => selectedBranch], ([googleMapsContainer, selectedBranch]) => {
                            if (googleMapsContainer && selectedBranch) {
                                initGoogleMaps()
                                stopWatching()
                            }
                        })

                        watch(selectedBranch, (branch) => {
                            if (customMarker && branch) {
                                customMarker.position = {
                                    lat: branch.latitude,
                                    lng: branch.longitude
                                }
                                googleMap.setCenter({
                                    lat: branch.latitude,
                                    lng: branch.longitude
                                })
                                infoWindow.setContent(infoWindowContentString.value)
                                isDrawerOpen.value = false
                            }
                        })

                        watch([isDrawerOpen, searchInputRef], ([isOpen, input]) => {
                            if (isOpen) {
                                nextTick(() => {
                                    input?.focus()
                                })
                            }
                        })

                        watch(colorStore, (val) => {
                            initGoogleMaps()
                        })

                        onSearch()

                        return {
                            searchTerm,
                            selectedBranch,
                            selectedBranchKey,
                            isBusy,
                            branches,
                            googleMapsRef,
                            searchInputRef,
                            mdOrGreater,
                            isDrawerOpen,
                            onSearch
                        }
                    }
                }),
            )

            branchLocatorVueApp.mount(branchLocatorEl)


            onTeardown(() => {
                branchLocatorVueApp.unmount()
            })
        }

        onInView(() => {
            setupGoogleMaps()
        })
    }
})